import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {FlightCheckModel} from '@taopix/taopix-core/dist/types/Reducers/FlightCheckReducer';
import {
  Button,
  HelpTip,
  Horizontal,
  OptionsIcon,
  SwapIcon,
  ThemeName,
  Vertical,
} from '@taopix/taopix-design-system';
import {UserFlowStep} from '../../Facade/UserFlow/BaseUserFlow';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';
import useSettings from '../../Hooks/useSettings';
import {LayoutSelector, TotalPageCountSelector} from '../../Selectors';
import AppMenu, {AppMenuItem} from '../AppMenu/AppMenu';
import {useChangeProductContext} from '../Context/ChangeProduct/ChangeProductContext';
import {useUserFlowState} from '../Context/UserFlow/UserFlowContext';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {useExperience} from '../Experience/ExperienceProvider';
import SaveButton from '../Save/SaveButton';
import ShareButton from '../Share/ShareButton';
import AppHeader from './AppHeader';
import Logo from './Logo';
import OrderButton from './OrderButton';
import ProjectInfoButton from './ProjectInfoButton';
import UploadProgressButton from './UploadProgressButton';
import ViewModeSelector from '../ViewModeSelector/ViewModeSelector';
import UndoRedoButtons from '../UndoRedoButtons/UndoRedoButtons';

export type EditorHeaderTypes = {
  onClickItem: (item: AppMenuItem) => void;
  projectPrice: string;
};

export type HistoryState = {
  undo: boolean;
  redo: boolean;
};

const EditorHeader = ({onClickItem, projectPrice}: EditorHeaderTypes) => {
  const settings = useSettings();
  const ui = useUiState();
  const userFlow = useUserFlowState();
  const changeProduct = useChangeProductContext();
  const {experience} = useExperience();

  // pull in language support
  const {t} = useTranslation(['flightcheck', 'optionsandextras']);
  const productLayout = useSelector(LayoutSelector);
  const pageCount = useSelector(TotalPageCountSelector);

  const [showFlightCheckButton, setShowFlightCheckButton] = useState<boolean>(false);
  const [errorCount, setErrorCount] = useState<number>(0);
  const [showFlightCheckTip, setShowFlightCheckTip] = useState<boolean>(false);

  const auth = settings.state?.authentication;
  const showSaveButton = experience.EDITOR.header.save || false;
  const appMenuSaveButton = experience.SETTINGS.appMenu.saveButton || false;
  const showShareProjectButton = experience.EDITOR.header.shareProject || false;
  const showAppLogo = experience.EDITOR.header.logo;
  const showChangeProductButton =
    experience.EDITOR.header.changeProduct && changeProduct.state.canChangeProduct;
  const [showUndoRedoButtons, setShowUndoRedoButtons] = useState<boolean>(false);
  const [showViewModeSelector, setShowViewModeSelector] = useState<boolean>(false);

  const errors: FlightCheckModel = useSelector<any>(state => state?.flightcheck) as any;

  const criticalErrorsCount = Object.keys(errors.criticalErrors).length;
  const minorErrorCount = Object.keys(errors.minorErrors).length;
  const stepActive = userFlow.isStepActive(
    UserFlowType.FlightCheck,
    UserFlowStep.FlightCheckIssueFix
  );

  useEffect(() => {
    setErrorCount(criticalErrorsCount + minorErrorCount);
  }, [criticalErrorsCount, minorErrorCount]);

  useEffect(() => {
    if (ui.state.isSmallScreen && stepActive) {
      setShowFlightCheckButton(true);
      setShowFlightCheckTip(true);
    } else {
      setShowFlightCheckButton(false);
      setShowFlightCheckTip(false);
    }
  }, [ui.state.isSmallScreen, stepActive]);

  useEffect(() => {
    // Update the visibility of the undo/redo and view mode buttons when the screen size changes
    setShowUndoRedoButtons(
      ui.state.isSmallScreen
        ? experience.EDITOR.header.undoRedoButtons.undoRedoSmall
        : experience.EDITOR.header.undoRedoButtons.undoRedoLarge
    );

    setShowViewModeSelector(
      ui.state.isSmallScreen
        ? experience.EDITOR.header.viewModeSelector.viewModeSelectorSmall
        : experience.EDITOR.header.viewModeSelector.viewModeSelectorLarge
    );
  }, [ui.state.isSmallScreen]);

  const onClickMenuItem = (item: AppMenuItem) => {
    if (onClickItem) {
      onClickItem(item);
    }

    if (AppMenuItem.ChangeProduct === item) {
      showAlternativeProducts();
    }
  };

  // show alternative products
  const showAlternativeProducts = () => {
    // if there are alt layouts set userFlow to show change product dialog
    if (changeProduct.state.alternativeProducts.length > 0) {
      userFlow.activateUserFlow(UserFlowType.Change);
      return;
    }
  };

  const leftSideHeaderContent = (
    <>
      <AppMenu
        onClickItem={onClickMenuItem}
        authentication={auth || {}}
        showSave={false}
        showShare={false}
      />
      {!ui.state.isSmallScreen && showAppLogo && <Logo />}
    </>
  );

  const middleContent = (
    <>
      {(showUndoRedoButtons || showViewModeSelector) && (
        <div className="flex sm:ml-xxs space-x-xxs">
          {showUndoRedoButtons && (
            <div className={'flex space-x-[1px] self-center'}>
              <UndoRedoButtons
                buttonProps={{size: 'small', buttonStyle: 'tertiary', hideLabel: true}}
              />
            </div>
          )}
          {showViewModeSelector && (
            <div className={'flex space-x-[1px] self-center'}>
              <ViewModeSelector
                buttonProps={{size: 'small', buttonStyle: 'tertiary', hideLabel: true}}
                showAsDropDown
              />
            </div>
          )}
        </div>
      )}
    </>
  );

  const changeProductButtonRef = useRef<HTMLButtonElement>(null);

  const rightSideHeaderContent = (
    <>
      {!ui.state.isSmallScreen && showChangeProductButton && (
        <Button
          label={t('*:str_ButtonChangeProduct')}
          buttonStyle={'standard'}
          size={'small'}
          stacked
          startIcon={<SwapIcon />}
          onClick={showAlternativeProducts}
          ref={changeProductButtonRef}
          labelLines={1}
          className={'shrink-0 offsetFocus'}
        />
      )}

      <ShareButton
        className="max-w-[100rem] offsetFocus"
        showShareProjectButton={!ui.state.isSmallScreen && showShareProjectButton}
      />

      <UploadProgressButton />

      {showSaveButton &&
        (!ui.state.isSmallScreen || (ui.state.isSmallScreen && !appMenuSaveButton)) && (
          // Show the save button in large screen if enabled in the editor header configuration
          // Only show here on small screen if enabled AND the app menu save button is hidden
          <SaveButton className="max-w-[100rem] offsetFocus" onProjectSaved={() => {}} />
        )}

      {showFlightCheckButton && (
        <Button
          id={'flightcheck-issues-button'}
          buttonStyle={'distinct'}
          size={experience.EDITOR.header.headerHeight === 'sm' ? 'small' : 'medium'}
          label={t('flightcheck:str_LabelShowFlightcheckIssues', {
            defaultValue: 'Show flightcheck issues',
          })}
          hideLabel
          startIcon={<OptionsIcon />}
          badgeContent={String(errorCount)}
          badgePosition={'top-left'}
          badgeStyle={ThemeName.Notification}
          className="offsetFocus"
          onClick={() => {
            if (ui.state.palette.section !== 'flight-check') {
              setShowFlightCheckTip(false);
              ui.dispatch(UserInterfaceActions.setToolPaletteSection('flight-check'));
            } else {
              ui.dispatch(UserInterfaceActions.setToolPaletteSection(null));
            }
          }}
        />
      )}
      <HelpTip
        open={showFlightCheckTip && !ui.state.focusMode}
        id={'FlightCheckReviewHelpTip-sm'}
        message={t('str_MessageReviewIssueSmallScreen', {
          ns: 'flightcheck',
        })}
        anchorId={'flightcheck-issues-button'}
        anchorOrigin={{
          vertical: Vertical.Bottom,
          horizontal: Horizontal.Centre,
        }}
        transformOrigin={{
          vertical: Vertical.Top,
          horizontal: Horizontal.Centre,
        }}
        popDirection={'down'}
        offset={{x: 0, y: 10}}
        arrow
        className={'max-w-[240px]'}
        showAfter={500}
        hideAfter={5000}
        onClose={() => {
          setShowFlightCheckTip(false);
        }}
      />
      <ProjectInfoButton
        id={'header'}
        perms={experience.EDITOR.header}
        projectPrice={projectPrice}
        pageCount={pageCount}
        projectName={settings.state.project.name}
        productName={productLayout.name}
        changeProductFunction={showChangeProductButton ? showAlternativeProducts : undefined}
      />
      <OrderButton />
    </>
  );

  return (
    <AppHeader
      id="editor-app-header"
      leftSideContent={leftSideHeaderContent}
      middleContent={middleContent}
      rightSideContent={rightSideHeaderContent}
    />
  );
};

export default EditorHeader;
